import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import useOfferDesign from 'api/useOfferDesign';
import usePermissions from 'api/usePermissions';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { EFeatureFlag } from 'constants/enums';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { EToasterStatus, showToast } from 'utils/showToast';
import { BUTTON_TEXT, OFFER_TYPE_FILTER_OPTIONS, ROUTE_SELECT_TYPE } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignRow, OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { resolveOfferDesignType } from 'views/OfferDesign/OfferDesign.utils';

import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';
import PortalHeader from 'components/PortalHeader/PortalHeader';

import { Filters } from './components/Filters/Filters';
import { TableContent } from './components/TableContent/TableContent';
import { useOfferDesignColumns } from './hooks/useOfferDesignColumns';
import { useOfferDesignFilters } from './hooks/useOfferDesignFilters';

const OfferDesignTable = () => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { getOfferDesigns, deleteOfferDesign } = useOfferDesign(publisherId);
  const { canUserEdit } = usePermissions();
  const { isActionEnabled } = useUserPermissions();
  const { getFeatureFlagByKey } = useFeatureFlags({ publisherId });

  const isTriggeredPopupEnabled = getFeatureFlagByKey(EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU);
  const isBuyOneGetManyEnabled = getFeatureFlagByKey(
    EFeatureFlag.DASHBOARD_ROLLING_OFFER_1N_ENABLED
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOfferDesign, setSelectedOfferDesign] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    };
  }, [isDeleteModalOpen]);

  const canManage = isActionEnabled(
    null,
    EPermissionGroup.OFFER_DESIGN,
    EPermissionAction.MANAGE,
    publisherId
  );

  const offers: OfferDesignRow[] = useMemo(() => {
    if (getOfferDesigns.isLoading || !getOfferDesigns.data) return [];
    const result: OfferDesignRow[] = getOfferDesigns.data.result;
    return result.filter((o) => {
      const offerDesignType = resolveOfferDesignType(o.offerUiType, o.offerUiSubType);
      if (!isTriggeredPopupEnabled && offerDesignType === OfferDesignTypes.TriggeredPopup) {
        return false;
      }
      if (!isBuyOneGetManyEnabled && offerDesignType === OfferDesignTypes.BuyOneGetMany) {
        return false;
      }
      return true;
    });
  }, [getOfferDesigns.data, getOfferDesigns.isLoading, isTriggeredPopupEnabled, isBuyOneGetManyEnabled]);

  const typeFilterOptions = useMemo(
    () => OFFER_TYPE_FILTER_OPTIONS.filter((o) => {
      if (!isTriggeredPopupEnabled && o.value === OfferDesignTypes.TriggeredPopup) {
        return false;
      }
      if (!isBuyOneGetManyEnabled && o.value === OfferDesignTypes.BuyOneGetMany) {
        return false;
      }
      return true;
    }),
    [isTriggeredPopupEnabled, isBuyOneGetManyEnabled]
  );

  const {
    externalIdFilter,
    nameFilter,
    typeFilter,
    filteredOffers,
    handleExternalIdChange,
    handleNameChange,
    handleTypesSelectionChange,
    handleClearFilters
  } = useOfferDesignFilters(offers);

  const actions = useMemo(
    () => ({
      onEdit: (id: string, offerUiType: string, offerUiSubType?: string) =>
        navigate(`./form/${id}?type=${resolveOfferDesignType(offerUiType, offerUiSubType)}`),
      onDuplicate: (id: string, offerUiType: string, offerUiSubType?: string) =>
        navigate(`./dup/${id}?type=${resolveOfferDesignType(offerUiType, offerUiSubType)}`),
      onDelete: (id: string) => {
        setSelectedOfferDesign(id);
        setIsDeleteModalOpen(true);
      }
    }),
    [navigate]
  );

  const permissions = useMemo(
    () => ({ canUserEdit, canManage }),
    [canUserEdit, canManage]
  );

  const { columns } = useOfferDesignColumns({
    isLoading: getOfferDesigns.isLoading,
    actions,
    permissions
  });

  const handleCreate = () => navigate(`./${ROUTE_SELECT_TYPE}`);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  const handleDeleteOfferDesign = useCallback(async () => {
    if (!selectedOfferDesign) return;
    await deleteOfferDesign.mutate(selectedOfferDesign, {
      onSuccess: () => {
        showToast({
          message: 'Offer Design deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOfferDesigns.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete Offer Design',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setIsDeleteModalOpen(false);
        setSelectedOfferDesign(null);
      }
    });
  }, [selectedOfferDesign, deleteOfferDesign, getOfferDesigns]);

  return (
    <div className="overflow-y-scroll opacity-100 pointer-events-auto">
      <PortalHeader
        title="Offer Design"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={handleCreate}
        primaryButtonState={{
          disabled: !canUserEdit,
          hidden: !canManage
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <Filters
          externalIdFilter={externalIdFilter}
          nameFilter={nameFilter}
          typeFilter={typeFilter}
          typeFilterOptions={typeFilterOptions}
          onExternalIdChange={handleExternalIdChange}
          onNameChange={handleNameChange}
          onTypesSelectionChange={handleTypesSelectionChange}
          onClearFilters={handleClearFilters}
        />
      )}
      <TableContent
        isLoading={getOfferDesigns.isLoading || getOfferDesigns.isFetching}
        offers={offers}
        filteredOffers={filteredOffers}
        columns={columns}
        canManage={canManage}
        onCreate={handleCreate}
      />
      {isDeleteModalOpen && (
        <DialogModal
          isOpen={isDeleteModalOpen}
          onOpenChange={handleCloseDeleteModal}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_DESIGN_POPUP].title}
          description={
            modalConfigs[PORTAL_MODAL_NAMES.DELETE_OFFER_DESIGN_POPUP].description
          }
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOfferDesign },
            onClose: {
              label: 'Cancel',
              action: handleCloseDeleteModal,
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default OfferDesignTable;
